/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import { Map, List } from 'immutable';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { useTheme, lighten } from '@mui/material/styles';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const DEFAULT_DATE_FORMAT_OPTIONS = {
  weekday: 'long',
  month:   'long',
  day:     'numeric',
};

/* ––
 * ––– Component
 * –––––––––––––––––––––––––––––––– */
function AppointmentSlotPicker({ date, onSlotSelected }) {
  /* –– Resources
   * –––––––––––––––––––––––––––––––– */
  const theme = useTheme();

  /* –– Render
   * –––––––––––––––––––––––––––––––– */
  const formattedDate = useMemo(() => {
    if (!Map.isMap(date) || !date.get('date')) { return ''; }

    return date.get('date').toLocaleString(DEFAULT_DATE_FORMAT_OPTIONS);
  }, [date]);

  const slotComponents = useMemo(() => {
    if (!Map.isMap(date) || !List.isList(date.get('slots'))) { return null; }

    return date.get('slots').map((slot) => {
      if (!Map.isMap(slot)) { return null; }

      const formattedStartTime = slot.get('startTime').toLocaleString(DateTime.TIME_SIMPLE);
      const formattedEndTime = slot.get('endTime').toLocaleString(DateTime.TIME_SIMPLE);

      const selectSlot = () => onSlotSelected(slot);

      return (
        <ButtonBase
          key={slot.get('id')}
          onClick={selectSlot}
          disabled={!slot.get('available')}
          disableRipple
          sx={{
            width:        '100%',
            textAlign:    'center',
            padding:      2,
            borderRadius: 1.5,
            borderWidth:  1.5,
            borderStyle:  'solid',
            borderColor:  'grey.500',
            transition:   theme.transitions.create(
              ['background-color', 'border-color', 'color'],
              {
                duration: theme.transitions.duration.short,
                easing:   theme.transitions.easing.easeOut,
              },
            ),
            '&:hover': {
              backgroundColor: lighten(theme.palette.primary.main, 0.9),
              borderColor:     'primary.main',
            },
            '&:focus': {
              backgroundColor: lighten(theme.palette.primary.main, 0.9),
              borderColor:     'primary.main',
            },
            '&:active': {
              backgroundColor: 'primary.main',
              borderColor:     'primary.main',
              color:           'primary.contrastText',
            },
            '&:disabled': {
              backgroundColor: 'background.dark',
              opacity:         0.5,
            },
          }}
          data-testid={`appointment-slot-${slot.get('id')}`}
        >
          <Typography variant="body" fontWeight="medium" fontFamily={theme.typography.fontFamily}>
            {formattedStartTime}
            {' '}
            -
            {' '}
            {formattedEndTime}
            {' '}
            (
            {slot.get('startTime').offsetNameShort}
            )
          </Typography>
        </ButtonBase>
      );
    });
  }, [date, onSlotSelected]);

  if (!Map.isMap(date)) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" textAlign="center" paddingY={4}>
        <Typography variant="h5" textAlign="center" fontWeight="medium">{t('eos.landing_page.text.booking.no_date_selected')}</Typography>
      </Box>
    );
  }

  return (
    <Box pt={{ xs: 2, md: 2.5 }}>
      <Typography variant="h3" textAlign="center" fontWeight="medium">{formattedDate}</Typography>
      <Box display="flex" flexDirection="column" gap={2} paddingY={2}>
        {slotComponents}
      </Box>
    </Box>
  );
}

/* ––
 * ––– Props declaration
 * –––––––––––––––––––––––––––––––– */
AppointmentSlotPicker.propTypes = {
  date:           PropTypes.oneOfType([PropTypes.instanceOf(Map), PropTypes.oneOf([null])]),
  onSlotSelected: PropTypes.func.isRequired,
};

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default AppointmentSlotPicker;
